<template>
  <div class="flex flex-row">
    <div class="relative">
      <BaseMemberAvatar
        class="cursor-pointer"
        size="160"
        :member="userStore.user"
        omit-tooltip
        @click="() => openFileBrowser()"
      />
      <div class="absolute right-0 bottom-0">
        <BaseIconButton color="filled" icon="edit" @click="() => openFileBrowser()" />
      </div>
    </div>
  </div>

  <BaseDialog :is-open="isOpen" type="basic">
    <div class="flex flex-col items-center p-6">
      <div
        class="h-40 w-40 overflow-hidden rounded-full bg-cover bg-center"
        :style="{ 'background-image': 'url(' + previewUrl + ')' }"
      ></div>
    </div>

    <template #confirm>
      <BaseTextButton v-if="file" color="filled" @click="uploadFile">Bild hochladen</BaseTextButton>
    </template>

    <template #dismiss>
      <BaseTextButton color="text" @click="close">Abbrechen</BaseTextButton></template
    >
  </BaseDialog>
</template>
<script lang="ts" setup>
  import { ref, watch } from 'vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import usePost from '../../../core/composables/api/usePost'
  import { useFileDialog } from '@vueuse/core'
  import { useAvatarStore } from '../../../stores/avatar.store'
  import { useUserStore } from '../../../stores/user.store'
  import BaseMemberAvatar from '../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseIconButton from '../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import { useSnackStore } from '../../../stores/snack.store'
  import { useI18n } from 'vue-i18n'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'

  const avatarStore = useAvatarStore()
  const userStore = useUserStore()

  const { t } = useI18n()

  const { isOpen, openDialog, closeDialog } = useDialog()
  const { mutate, status: uploadStatus } = usePost<string, FormData>('uploadAvatar')

  const file = ref<File | null>(null)

  const previewUrl = ref<string | null>(null)

  const {
    open: openFileBrowser,
    reset,
    onCancel,
    onChange,
  } = useFileDialog({
    accept: 'image/*', // Set to accept only image files
  })

  const close = () => {
    reset()
    closeDialog()
  }

  const snackStore = useSnackStore()

  onChange((files) => {
    if (files && files[0]) {
      validateFile(files[0])
      if (errors.value.length == 0) {
        file.value = files[0]
        const reader = new FileReader()
        reader.readAsDataURL(files[0])
        reader.onload = (e) => {
          previewUrl.value = e.target?.result as string
        }
        openDialog()
      } else {
        snackStore.showError(errors.value.join(', '))
      }
    }
  })

  const MAX_FILESIZE = 3 * 1000 * 1000

  const errors = ref<string[]>([])

  const validateFile = (file: File) => {
    errors.value = []
    if (file.size > MAX_FILESIZE) {
      errors.value.push(t('settings.avatar.errors.too-big'))
    }
    if (!file.type.startsWith('image/')) {
      errors.value.push(t('settings.avatar.errors.wrong-type'))
    }
  }

  onCancel(() => {
    previewUrl.value = null
  })

  const uploadFile = () => {
    if (file.value) {
      const formData = new FormData()
      formData.append('file', file.value)

      mutate(formData)
    }
  }

  watch(uploadStatus, async () => {
    if (uploadStatus.value == ApiRequestStatus.Success) {
      await userStore.loadUser()
      avatarStore.resetMemberId(userStore.user.id)
      closeDialog()
    }
  })
</script>
